/** Phase 2.3 — header pills + closing banner + ended screen + rename + audio settings.
 *
 * Kept in one file so Room.jsx imports a single module instead of 7. All
 * pieces are pure presentation + small effect hooks; the data plumbing
 * (roomMeta, role flags, API calls) is passed in by Room.jsx.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Clock, ShieldCheck, Headphones, Lock, Unlock, DoorClosed, Loader2,
  Mic, Camera as CameraIcon, Volume2, ChevronDown,
  RefreshCw, X, AlertTriangle,
} from "lucide-react";

/** Format ms → "HH:MM:SS" or "DD:HH:MM:SS" if over 24h. */
function fmtElapsed(ms) {
  if (!Number.isFinite(ms) || ms < 0) ms = 0;
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  if (d > 0) return `${pad(d)}:${pad(h)}:${pad(m)}:${pad(ss)}`;
  return `${pad(h)}:${pad(m)}:${pad(ss)}`;
}

export function MeetingTimer({ startedAt }) {
  const start = useMemo(() => {
    if (!startedAt) return null;
    const d = new Date(startedAt);
    return Number.isFinite(d.getTime()) ? d.getTime() : null;
  }, [startedAt]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!start) return null;
  return (
    <div
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 font-mono text-[11px] tabular-nums text-white/85"
      title="Time this current room event has been open"
      data-testid="meeting-timer"
    >
      <Clock className="w-3.5 h-3.5 text-[#00F0FF]" />
      <span className="hidden xl:inline text-white/50 uppercase tracking-[0.12em]">Current session</span>
      <span data-testid="meeting-timer-value">{fmtElapsed(now - start)}</span>
    </div>
  );
}

const SECURITY_POINTS = [
  { ok: true, text: "HTTPS encrypted connection" },
  { ok: true, text: "Cloudflare anti-bot (Turnstile) at entry" },
  { ok: true, text: "Device fingerprint ban enforcement" },
  { ok: true, text: "Server-side moderation (LiveKit SFU + ban-by-device)" },
  { ok: true, text: "Closed rooms cannot be re-entered" },
  { ok: "warn", text: "Recording protection: Limited in browser. Full protection in our desktop app (Phase 5)." },
];

export function SecurityShield() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-500/15 hover:bg-green-500/25 border border-green-400/40 transition-colors"
        title="Security: Live · click for details"
        data-testid="security-shield-btn"
      >
        <ShieldCheck className="w-4 h-4 text-green-400" strokeWidth={2.5} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-[calc(100%+6px)] z-40 w-[340px] max-w-[92vw] rounded-lg bg-[#0A0A0F] border border-white/15 shadow-2xl p-3"
          data-testid="security-shield-popover"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-green-400 font-bold">
              <ShieldCheck className="w-3.5 h-3.5" /> Security · Live
            </div>
            <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white/80">
              <X className="w-4 h-4" />
            </button>
          </div>
          <ul className="space-y-1.5 text-[12px] leading-snug">
            {SECURITY_POINTS.map((p, i) => (
              <li key={i} className="flex gap-2">
                <span className={p.ok === true ? "text-green-400" : "text-amber-400"} aria-hidden="true">
                  {p.ok === true ? "✅" : "⚠️"}
                </span>
                <span className={p.ok === true ? "text-white/80" : "text-amber-200/80"}>{p.text}</span>
              </li>
            ))}
          </ul>
          <div className="mt-2 pt-2 border-t border-white/10 text-[10px] text-white/40">
            Phase 4 adds: Spam bot active · Liveness agent active
          </div>
        </div>
      )}
    </div>
  );
}

/** Centralized localStorage keys */
const LS = {
  out: "sr_audio_output_id",
  cam: "sr_video_input_id",
  mic: "sr_audio_input_id",
};

/** Apply a sinkId (output device) to every audio element in the page. Returns
 *  true on full success, false if any element refused (Firefox/Safari).
 */
export async function applySinkToAllAudio(sinkId) {
  if (!sinkId) return true;
  const all = document.querySelectorAll("audio,video");
  let ok = true;
  for (const el of all) {
    try {
      if (typeof el.setSinkId === "function") await el.setSinkId(sinkId);
    } catch (e) {
      ok = false;
      // eslint-disable-next-line no-console
      console.warn("[SR] setSinkId failed on", el, e);
    }
  }
  return ok;
}

export function AudioSettings({ isDj }) {
  const [open, setOpen] = useState(false);
  const [outs, setOuts] = useState([]);
  const [cams, setCams] = useState([]);
  const [mics, setMics] = useState([]);
  const [sel, setSel] = useState({
    out: localStorage.getItem(LS.out) || "default",
    cam: localStorage.getItem(LS.cam) || "default",
    mic: localStorage.getItem(LS.mic) || "default",
  });
  const [sinkSupported] = useState(() =>
    typeof HTMLMediaElement !== "undefined" &&
    typeof HTMLMediaElement.prototype.setSinkId === "function"
  );

  const refreshDevices = useCallback(async () => {
    try {
      const ds = await navigator.mediaDevices.enumerateDevices();
      setOuts(ds.filter((d) => d.kind === "audiooutput"));
      setCams(ds.filter((d) => d.kind === "videoinput"));
      setMics(ds.filter((d) => d.kind === "audioinput"));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[SR] enumerateDevices failed", e);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    refreshDevices();
    const onChange = () => refreshDevices();
    try { navigator.mediaDevices.addEventListener("devicechange", onChange); } catch { /* ignore */ }
    return () => { try { navigator.mediaDevices.removeEventListener("devicechange", onChange); } catch { /* ignore */ } };
  }, [open, refreshDevices]);

  // Apply persisted output on first mount (after audio elements exist).
  useEffect(() => {
    if (!sinkSupported || sel.out === "default") return;
    applySinkToAllAudio(sel.out);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOutput = async (id) => {
    setSel((s) => ({ ...s, out: id }));
    localStorage.setItem(LS.out, id);
    if (sinkSupported) await applySinkToAllAudio(id === "default" ? "" : id);
  };
  const setCam = (id) => {
    setSel((s) => ({ ...s, cam: id }));
    localStorage.setItem(LS.cam, id);
    // Functional swap deferred — surface for next track/republish.
  };
  const setMic = (id) => {
    setSel((s) => ({ ...s, mic: id }));
    localStorage.setItem(LS.mic, id);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
        title="Audio settings · input + output devices"
        data-testid="audio-settings-btn"
      >
        <Headphones className="w-4 h-4 text-white/80" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-[calc(100%+6px)] z-40 w-[360px] max-w-[92vw] rounded-lg bg-[#0A0A0F] border border-white/15 shadow-2xl p-3"
          data-testid="audio-settings-popover"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">Audio settings</div>
            <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white/80"><X className="w-4 h-4" /></button>
          </div>

          <DeviceRow
            icon={<Volume2 className="w-3.5 h-3.5" />}
            label="Output (speaker/headphones)"
            value={sel.out} options={outs} onChange={setOutput}
            note={!sinkSupported ? "Output switching not supported on this browser (Firefox / Safari). Will use system default." : null}
            testid="audio-out-select"
          />
          <DeviceRow
            icon={<CameraIcon className="w-3.5 h-3.5" />}
            label="Camera"
            value={sel.cam} options={cams} onChange={setCam}
            note="Applied on next publish."
            testid="audio-cam-select"
          />
          {isDj && (
            <DeviceRow
              icon={<Mic className="w-3.5 h-3.5" />}
              label="Microphone (DJ live mic)"
              value={sel.mic} options={mics} onChange={setMic}
              note="Saved for next track / live mic mode (full hot-swap lands in Phase 2.4)."
              testid="audio-mic-select"
            />
          )}

          {outs.length === 0 && cams.length === 0 && (
            <div className="text-[11px] text-white/40 mt-2">
              Devices appear after you've granted camera access at least once.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DeviceRow({ icon, label, value, options, onChange, note, testid }) {
  return (
    <div className="mb-3 last:mb-0">
      <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold mb-1">
        {icon}<span>{label}</span>
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none bg-white/5 border border-white/15 rounded-md px-2.5 py-2 text-sm text-white pr-7"
          data-testid={testid}
        >
          <option value="default">System Default</option>
          {options.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || "(unnamed device)"}
            </option>
          ))}
        </select>
        <ChevronDown className="w-3.5 h-3.5 text-white/40 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
      {note && <div className="text-[10px] text-white/35 mt-1 leading-snug">{note}</div>}
    </div>
  );
}

export function LockButton({ locked, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] uppercase tracking-[0.15em] font-bold transition-colors ${
        locked
          ? "bg-amber-400/15 border-amber-400/50 text-amber-200"
          : "bg-white/5 border-white/15 text-white/70 hover:text-white"
      }`}
      title={locked ? "Room is locked · click to unlock" : "Lock this room — block new joins"}
      data-testid="lock-room-btn"
    >
      {locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
      <span className="hidden md:inline">{locked ? "Locked" : "Lock"}</span>
    </button>
  );
}

export function ClosingControls({ closingAt, onBegin, onCancel, onEndNow }) {
  if (closingAt) {
    return (
      <div className="flex items-center gap-1.5">
        <button
          onClick={onCancel}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/15 text-[11px] uppercase tracking-[0.15em] font-bold text-white/80"
          data-testid="cancel-closing-btn"
        >
          <X className="w-3.5 h-3.5" /><span className="hidden md:inline">Cancel</span>
        </button>
        <button
          onClick={onEndNow}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#FF007F] hover:bg-[#FF007F]/85 text-white text-[11px] uppercase tracking-[0.15em] font-bold"
          data-testid="end-now-btn"
        >
          End now
        </button>
      </div>
    );
  }
  return (
    <button
      onClick={onBegin}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/15 text-[11px] uppercase tracking-[0.15em] font-bold text-white/80"
      title="Start a 60-second soft close"
      data-testid="begin-closing-btn"
    >
      <DoorClosed className="w-3.5 h-3.5" /><span className="hidden md:inline">Begin closing</span>
    </button>
  );
}

export function ClosingBanner({ closingAt, hostName }) {
  const [secondsLeft, setSecondsLeft] = useState(60);
  useEffect(() => {
    if (!closingAt) return;
    const target = new Date(closingAt).getTime();
    const tick = () => {
      const s = Math.max(0, Math.round((target - Date.now()) / 1000));
      setSecondsLeft(s);
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [closingAt]);
  if (!closingAt) return null;
  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  return (
    <div
      className="sticky top-0 z-40 bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] px-4 py-2 text-sm font-bold tracking-wide flex items-center justify-center gap-3 shadow-lg"
      data-testid="closing-banner"
    >
      <AlertTriangle className="w-4 h-4" />
      <span data-testid="closing-banner-text">
        {hostName ? `${hostName} is closing this room in` : "This room is closing in"}{" "}
        <span className="font-mono tabular-nums" data-testid="closing-countdown">{fmt(secondsLeft)}</span>
      </span>
    </div>
  );
}

export function EndedScreen({ roomId, endedAt, canReopen = false, onReopen, onLeave }) {
  const fmtWhen = endedAt ? new Date(endedAt).toLocaleString() : "just now";
  return (
    <div className="min-h-screen sr-aurora grid place-items-center px-6 py-10" data-testid="ended-screen">
      <div className="sr-glass rounded-2xl p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/5 border border-white/15 grid place-items-center">
          <DoorClosed className="w-7 h-7 text-white/60" />
        </div>
        <h2 className="font-display text-2xl font-bold mb-1" data-testid="ended-title">{canReopen ? "This event is closed" : "This room has ended"}</h2>
        <p className="text-white/60 text-sm mb-1">Closed <span className="text-white/80">{fmtWhen}</span>.</p>
        <p className="text-white/40 text-xs mb-3 font-mono">{roomId}</p>
        <p className="text-white/50 text-xs mb-6">
          {canReopen
            ? "This is a permanent room space. Reopen it when the next event is ready."
            : "This temporary room has ended. Create a new temporary room for another session."}
        </p>
        <div className="flex gap-3">
          <a
            href="/rooms"
            className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/15 rounded-lg text-sm font-bold uppercase tracking-[0.12em] text-center"
            data-testid="ended-back-to-rooms"
          >Back to rooms</a>
          {canReopen ? (
            <button
              onClick={onReopen}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-[#00F0FF] to-[#B8FF00] text-[#0A0A0F] rounded-lg text-sm font-bold uppercase tracking-[0.12em]"
              data-testid="ended-reopen-btn"
            >Reopen room</button>
          ) : (
            <button
              onClick={onLeave}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] rounded-lg text-sm font-bold uppercase tracking-[0.12em]"
              data-testid="ended-leave-btn"
            >Go home</button>
          )}
        </div>
      </div>
    </div>
  );
}

export function RenameModal({ open, currentName, targetId, onClose, onConfirm }) {
  const [value, setValue] = useState(currentName || "");
  const ref = useRef(null);
  const panelRef = useRef(null);
  useEffect(() => { if (open) { setValue(currentName || ""); setTimeout(() => ref.current?.focus(), 50); } }, [open, currentName]);
  // Phase 3.4 — ESC + outside-click dismiss
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    const onClick = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) onClose?.(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur p-4" data-testid="rename-modal">
      <div ref={panelRef} className="sr-glass rounded-2xl p-5 border border-white/10 max-w-sm w-full">
        <h3 className="font-bold text-base mb-1">Rename participant</h3>
        <p className="text-xs text-white/50 mb-3">Set <span className="text-white">{currentName || "this participant"}</span>'s display name for this meeting. Everyone will see the new name.</p>
        <input
          ref={ref}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && value.trim()) onConfirm(targetId, value.trim()); }}
          maxLength={64}
          placeholder="New display name"
          className="w-full bg-white/5 border border-white/15 rounded-md px-3 py-2 text-sm text-white mb-3"
          data-testid="rename-input"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-xs font-bold bg-white/5 hover:bg-white/10 border border-white/15 rounded-md uppercase tracking-[0.12em]" data-testid="rename-cancel-btn">Cancel</button>
          <button
            onClick={() => value.trim() && onConfirm(targetId, value.trim())}
            disabled={!value.trim()}
            className="px-4 py-2 text-xs font-bold bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] rounded-md uppercase tracking-[0.12em] disabled:opacity-50"
            data-testid="rename-confirm-btn"
          >Save</button>
        </div>
      </div>
    </div>
  );
}
